/**
 * Descargar las facturas de un periodo en un ZIP, para la gestoría.
 *
 * Vive suelto y sin importar nada, como el resto de `lib/`.
 *
 * POR QUÉ EXISTE
 * --------------
 * Angel, 30-sep-2026: "necesito poder descargar las facturas en un zip todas
 * juntas porque las tengo que mandar a nuestra gestoría cada trimestre". Y
 * preguntado por el volumen: más de 800 al trimestre, pero prefiere sacarlas
 * mes a mes, unas 350 cada vez.
 *
 * Aquí están las cuentas de fechas y qué se manda. El ZIP lo monta el
 * servidor (`POST /invoices/export/zip`), que es quien tiene los PDF.
 */

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

/**
 * El último día de un mes: 31 en julio, 28 o 29 en febrero.
 *
 * `new Date(año, mes, 0)` da el último día del mes anterior, así que pasando
 * el mes SIN restarle uno se obtiene el último del mes pedido. Y es fiable en
 * años bisiestos, que es justo donde falla escribir un 28 a mano.
 */
function ultimoDia(anyo, mes) {
  return new Date(anyo, mes, 0).getDate();
}

const dosCifras = (n) => String(n).padStart(2, '0');

/**
 * El rango de un mes, tal y como lo espera el servidor.
 *
 * `mes` viene de un <input type="month">: "2026-07".
 */
export function rangoDelMes(mes) {
  const [anyo, numero] = String(mes || '').split('-').map(Number);
  if (!anyo || !numero || numero < 1 || numero > 12) return null;
  return {
    desde: `${anyo}-${dosCifras(numero)}-01`,
    hasta: `${anyo}-${dosCifras(numero)}-${ultimoDia(anyo, numero)}`,
  };
}

/** El rango de un trimestre natural: T3 de 2026 es del 1-jul al 30-sep. */
export function rangoDelTrimestre(anyo, trimestre) {
  const t = Number(trimestre);
  if (!anyo || !(t >= 1 && t <= 4)) return null;
  const primero = (t - 1) * 3 + 1;
  const ultimo = t * 3;
  return {
    desde: `${anyo}-${dosCifras(primero)}-01`,
    hasta: `${anyo}-${dosCifras(ultimo)}-${ultimoDia(anyo, ultimo)}`,
  };
}

/**
 * Qué mes representa este rango, o '' si no es un mes entero.
 *
 * Sirve para que el selector de mes se quede marcado cuando el rango lo puso
 * él, y se vacíe cuando alguien toca las fechas a mano. Un selector que dice
 * "Julio" mientras las fechas dicen otra cosa es peor que no tenerlo.
 */
export function mesDelRango({ desde, hasta } = {}) {
  if (!desde || !hasta) return '';
  const r = rangoDelMes(desde.slice(0, 7));
  return r && r.desde === desde && r.hasta === hasta ? desde.slice(0, 7) : '';
}

/** Cómo se lee un mes: "Julio 2026". */
export function nombreDelMes(mes) {
  const [anyo, numero] = String(mes || '').split('-').map(Number);
  if (!anyo || !numero || numero < 1 || numero > 12) return '';
  return `${MESES[numero - 1]} ${anyo}`;
}

/** Una fecha ISO como se lee aquí: "2026-07-01" -> "01/07/2026". */
function enCristiano(iso) {
  const [a, m, d] = String(iso || '').split('-');
  return a && m && d ? `${d}/${m}/${a}` : '';
}

/**
 * Qué periodo hay puesto, en una línea y en cristiano.
 *
 * Existe porque el botón de descargar dice "las 1.431 del filtro" y ese
 * número, solo, no dice de qué. Angel se bajó todo el histórico pensando que
 * iba un mes. Esta línea es la que lo habría parado.
 */
export function descripcionDelPeriodo({ desde, hasta } = {}) {
  if (!desde && !hasta) return 'Todas las fechas';
  if (desde && !hasta) return `Desde el ${enCristiano(desde)}`;
  if (!desde && hasta) return `Hasta el ${enCristiano(hasta)}`;

  const mes = mesDelRango({ desde, hasta });
  if (mes) return nombreDelMes(mes);

  const anyo = Number(desde.slice(0, 4));
  for (let t = 1; t <= 4; t += 1) {
    const r = rangoDelTrimestre(anyo, t);
    if (r && r.desde === desde && r.hasta === hasta) return `${t}º trimestre ${anyo}`;
  }
  return `Del ${enCristiano(desde)} al ${enCristiano(hasta)}`;
}

/**
 * El cuerpo del POST.
 *
 * Con facturas marcadas manda los ids y NADA MÁS: marcar a mano tiene que
 * ganar al filtro sin ambigüedad. Mandar las dos cosas dejaría al servidor
 * decidiendo cuál manda, y la respuesta a "¿qué me he descargado?" dependería
 * de leerse el código.
 */
export function cuerpoDeDescarga({ marcadas, filtros } = {}) {
  const ids = [...(marcadas || [])];
  if (ids.length) return { ids, incluir_listado: true };

  const f = filtros || {};
  return {
    desde: f.desde || null,
    hasta: f.hasta || null,
    // 'all' es "sin filtro" en la pantalla y null para el servidor.
    status: f.status && f.status !== 'all' ? f.status : null,
    search: f.search || null,
    is_recurring: f.recurring === 'recurring' ? true
      : f.recurring === 'unique' ? false : null,
    email_enviado: f.email === 'enviado' ? true
      : f.email === 'sin-enviar' ? false : null,
    incluir_listado: true,
  };
}

/** Lo que dice el botón, según haya marcadas o no. */
export function textoDelBoton({ marcadas, total } = {}) {
  const n = (marcadas || []).length ?? 0;
  if (n > 0) return `Descargar ${n} marcada${n === 1 ? '' : 's'}`;
  if (!total) return 'Descargar ZIP';
  return `Descargar las ${total} del filtro`;
}

/**
 * Por qué no se puede descargar todavía. Null cuando sí.
 *
 * El tope es el mismo que el del servidor. Se adelanta aquí porque bajarse
 * 2.000 facturas para que las rechace al final es una espera tirada.
 */
export const MAXIMO = 1500;

export function motivoParaNoDescargar({ marcadas, total } = {}) {
  const n = (marcadas || []).length;
  const cuantas = n || total || 0;
  if (!cuantas) return 'No hay ninguna factura que descargar.';
  if (cuantas > MAXIMO) {
    return `Son ${cuantas} facturas y el máximo por descarga es ${MAXIMO}. `
      + 'Acota el filtro, por ejemplo mes a mes.';
  }
  return null;
}

/**
 * El nombre del fichero que dice el servidor, o uno de reserva.
 *
 * Sin esto el navegador guarda el ZIP con el nombre de la URL ("zip"), y
 * Angel acabaría con tres ficheros llamados igual.
 */
export function nombreDelFichero(headers, reserva = 'facturas.zip') {
  const cabecera = (headers || {})['content-disposition']
    || (headers || {})['Content-Disposition'] || '';
  const encontrado = /filename="?([^";]+)"?/i.exec(cabecera);
  return encontrado ? encontrado[1].trim() : reserva;
}

/** Cuántas no se han podido generar, según lo que diga el servidor. */
export function fallidas(headers) {
  return Number((headers || {})['x-fallidas'] || 0) || 0;
}

export default cuerpoDeDescarga;
